import { IResponse, spErrorTags } from '@eventr/common'
import React, { createContext, useContext } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { EventrGatewayAPI } from '../../../data/gateway.api'
import { useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'

interface EditMembersProviderProps {
    children: React.ReactNode
}

interface IEditMembersContext {
    removeMember: (wpUserId: number) => Promise<IResponse<string>>
}

const EditMembersContext = createContext<IEditMembersContext | undefined>(
    undefined
)

export const EditMembersProvider: React.FC<EditMembersProviderProps> = ({
    children,
}) => {
    const { selectedOrg } = useBoundStore(
        useShallow((s) => {
            return { selectedOrg: s.selectedOrg }
        })
    )

    const removeMember = async (wpUserId: number) => {
        const resp = await EventrGatewayAPI.profile.removeMember(
            wpUserId,
            selectedOrg.name
        )
        if (resp.status !== 'success') {
            spErrorTags(resp.message, ['MEMBER'], resp)
            defaultAddSnack(resp.message, 'error')
        } else {
            useBoundStore.setState((prev) => {
                return {
                    ...prev,
                    users: prev.users.filter((u) => u.id !== wpUserId),
                }
            })
        }

        return resp
    }

    const contextValue: IEditMembersContext = {
        removeMember,
    }

    return (
        <EditMembersContext.Provider value={contextValue}>
            {children}
        </EditMembersContext.Provider>
    )
}

export const useEditMembers = (): IEditMembersContext => {
    const context = useContext(EditMembersContext)
    if (!context) {
        throw new Error(
            'useEditMembers must be used within an EditMembersProvider'
        )
    }
    return context
}
